import axios from "axios";
import { endpoint } from "./endpoint";
import { resetData } from "../features/Auth/AuthSlice";
import store from "../app/store";

const TEST_BYPASS_USER_ID = 17;

const getSessionAuth = () => store.getState()?.auth || {};

const getLatestToken = (authToken) => {
  return (
    sessionStorage.getItem("accessToken") ||
    (typeof authToken === "string" ? authToken : authToken?.token) ||
    getSessionAuth()?.token
  );
};

const buildHeaders = ({ reqHeaders, authToken }) => {
  const sessionAuth = getSessionAuth();
  const isTestUser = Boolean(sessionAuth.isTestUser);
  const activeToken = getLatestToken(authToken);
  const hasToken = Boolean(activeToken);

  const baseHeaders = {
    "Content-Type": reqHeaders?.["Content-Type"] || "application/json",
    Accept: "application/json",
    ...reqHeaders,
  };

  if (!hasToken) {
    return baseHeaders;
  }

  if (!isTestUser) {
    return {
      ...baseHeaders,
      Authorization: "Bearer " + activeToken,
    };
  }

  const { Authorization: _authorization, authorization: _authorizationLower, ...headersWithoutAuth } = baseHeaders;

  return {
    ...headersWithoutAuth,
    "x-test-user-id": TEST_BYPASS_USER_ID,
  };
};

// Dynamic Request Interceptor: Ensure Axios reads latest token dynamically on EVERY request
axios.interceptors.request.use(
  (config) => {
    const sessionAuth = getSessionAuth();
    const token = getLatestToken();
    if (token && !sessionAuth.isTestUser) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

async function createRequest({ reqHeaders, params, authToken }) {
  return axios.create({
    baseURL: endpoint.API_BASE_URL,
    responseType: "json",
    crossdomain: true,
    headers: buildHeaders({ reqHeaders, authToken }),
    params,
  });
}

export const handleCatchBlock = (error) => {
  console.log("Something went wrong fetching apis - ", error);
};


export async function apiHandler({
  url,
  method,
  headers: reqHeaders,
  data: jsonData,
  params,
  authToken,
  noCache = false,
}) {
  try {
    const activeToken = getLatestToken(authToken);
    if (url.includes("/queue-tasks") || url.includes("/image-generation") || url.includes("/generate")) {
      console.log(`[Generation API Token] ${method} ${url}:`, activeToken);
    }
    const request = await createRequest({ reqHeaders, params, authToken: activeToken });
    let result = [];
    switch (method) {
      case "POST":
        result = await request.post(url, jsonData);
        break;

      case "PUT":
        result = await request.put(url, jsonData);
        break;

      case "PATCH":
        result = await request.patch(url, jsonData);
        break;

      case "DELETE":
        result = await request.delete(url);
        break;

      case "GET":
        result = await request.get(url, noCache
          ? {
              headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
              },
              params: {
                ...params,
                _ts: Date.now(),
              },
            }
          : undefined);
        break;

      default:
        result = await request.get(url);
    }
    const { data, headers, status } = result;
    return { data, headers, status };
  } catch (error) {
    if (error.response?.status === 403) {
      const sessionAuth = getSessionAuth();
      if (authToken?.token && !sessionAuth.isTestUser) {
        store.dispatch(resetData());
        window.location.href = "/";
      }
    }
    handleCatchBlock(error);

    if (error.response) {
      const { data, headers, status } = error.response;
      return { data, headers, status };
    } else {
      const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
      return {
        data: {
          error_code: isOffline ? "OFFLINE" : 500,
          error_message: isOffline
            ? "You appear to be offline. Please check your internet connection."
            : "Error in getting data",
        },
      };
    }
  }
}
